import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import {
  useDeletePackage,
  useEvent,
  useSetPackageNominations,
  useSetPackageServices,
  useSetPackageState,
  useUpdatePackage,
} from '@/api/queries';
import type { EventCard, Package } from '@/api/types';
import { Button } from '@/components/kit/Button';
import {
  BandDot,
  ConditionItem,
  ConditionList,
  PackageItem,
  Switch,
} from '@/components/kit/Controls';
import { DateField } from '@/components/kit/DatePicker';
import { Form, NumberField } from '@/components/kit/Field';
import { Breadcrumbs, Columns, Divider, Kv, PageHeader, SectionLabel } from '@/components/kit/Layout';
import { MetricRow } from '@/components/kit/MetricRow';
import { Modal } from '@/components/kit/Modal';
import { Alert, EmptyState, ErrorState, Note, TableSkeleton } from '@/components/kit/Note';
import { Panel, Signals, Stack } from '@/components/kit/Panel';
import { Pill } from '@/components/kit/Pill';
import { PopoverSelect } from '@/components/kit/Popover';
import { useToast } from '@/components/kit/Toast';
import {
  ageRange,
  classes as classesPlural,
  date as fmtDate,
  fraction,
  money,
  nominations as nomPlural,
  num,
  sales as salesPlural,
  teams as teamsPlural,
} from '@/lib/format';
import { PACKAGE_KIND, PACKAGE_STATE, SERVICE_KIND } from './state';

/**
 * Карточка пакета — конструктор состава.
 *
 * Главное правило видно прямо на экране: **пакет с продажами можно
 * только расширять**. Купившие имеют право на то, что оплачивали;
 * чтобы поменять условия, пакет снимают с продажи и создают новый.
 * Крестики «убрать» при продажах не показываются, а сервер всё равно
 * откажет — здесь просто не предлагается то, что не сработает.
 */
export function PackagePage() {
  const { eventId, packageId } = useParams<{ eventId: string; packageId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const query = useEvent(eventId);
  const update = useUpdatePackage(eventId!);
  const setState = useSetPackageState(eventId!);
  const setServices = useSetPackageServices(eventId!);
  const setNominations = useSetPackageNominations(eventId!);
  const remove = useDeletePackage(eventId!);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addService, setAddService] = useState<string | null>(null);

  if (query.isError) {
    return (
      <div className="main">
        <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
      </div>
    );
  }

  if (query.isLoading || !query.data) {
    return (
      <div className="main">
        <PageHeader title="Пакет" />
        <TableSkeleton rows={6} cols={3} />
      </div>
    );
  }

  const event = query.data;
  const pack = event.packages.find((p) => p.id === packageId);

  if (!pack) {
    return (
      <div className="main">
        <EmptyState>Пакет не найден — возможно, его удалили</EmptyState>
      </div>
    );
  }

  const p = pack;
  const sold = p.soldQty > 0;
  const state = PACKAGE_STATE[p.state];
  const band = event.bands.find((b) => b.id === p.bandId);

  const services = p.serviceIds
    .map((id) => event.services.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  const ofKind = (kind: 'masterclass' | 'ticket' | 'showcase') =>
    services.filter((s) => s.kind === kind);

  const free = event.services.filter((s) => !p.serviceIds.includes(s.id));
  const defaults = p.nominations.filter((n) => n.isDefault);

  const fail = (e: Error) => toast.showError(e instanceof ApiError ? e.message : 'Не получилось');
  const save = (patch: Record<string, unknown>) =>
    update.mutate({ id: p.id, ...patch }, { onSuccess: () => toast.show('Сохранено'), onError: fail });

  /** Числовые поля — по окончании ввода; промис, чтобы поле откатилось при ошибке */
  const commit = (patch: Record<string, unknown>) =>
    update.mutateAsync({ id: p.id, ...patch }).then(
      () => toast.show('Сохранено'),
      (e: Error) => {
        fail(e);
        throw e;
      },
    );

  const goTab = (tab: string) => navigate(`/events/${event.id}/${tab}`);

  const changeState = (next: 'draft' | 'live' | 'stopped', message: string) =>
    setState.mutate(
      { id: p.id, state: next },
      { onSuccess: () => toast.show(message), onError: fail },
    );

  const removeService = (serviceId: string) =>
    setServices.mutate(
      { id: p.id, serviceIds: p.serviceIds.filter((s) => s !== serviceId) },
      { onSuccess: () => toast.show('Услуга убрана из состава'), onError: fail },
    );

  const addServiceToPack = (serviceId: string) =>
    setServices.mutate(
      { id: p.id, serviceIds: [...p.serviceIds, serviceId] },
      {
        onSuccess: () => {
          toast.show('Услуга добавлена в состав');
          setAddService(null);
        },
        onError: fail,
      },
    );

  const toggleNomination = (nominationId: string) => {
    const has = p.nominations.some((n) => n.nominationId === nominationId);
    const next = has
      ? p.nominations.filter((n) => n.nominationId !== nominationId)
      : [...p.nominations, { nominationId, isDefault: false }];

    setNominations.mutate({ id: p.id, nominations: next }, { onError: fail });
  };

  const toggleDefault = (nominationId: string) => {
    const next = p.nominations.map((n) =>
      n.nominationId === nominationId ? { ...n, isDefault: !n.isDefault } : n,
    );
    setNominations.mutate(
      { id: p.id, nominations: next },
      {
        onSuccess: () => toast.show('Пакет по умолчанию переназначен'),
        onError: fail,
      },
    );
  };

  const removeParticipation = () =>
    setNominations.mutate(
      { id: p.id, nominations: [] },
      { onSuccess: () => toast.show('Участие убрано из пакета'), onError: fail },
    );

  const raiseDate = p.raiseOn ?? event.priceRaiseDefault;
  const hot = Boolean(p.raiseAfterQty && p.soldFirstPriceQty / p.raiseAfterQty > 0.8);

  return (
    <div className="main">
      <Breadcrumbs
        items={[
          { label: 'Мероприятия', onClick: () => navigate('/events') },
          { label: event.city, onClick: () => goTab('packages') },
          { label: p.name },
        ]}
      />

      <PageHeader
        title={p.name}
        before={
          <>
            <Pill tone={state.tone}>{state.label}</Pill>
            <span className="dim">
              {PACKAGE_KIND[p.kind]}
              {p.visibleToClient ? '' : ' · скрытый'}
            </span>
          </>
        }
      >
        {p.state === 'draft' ? (
          <Button tone="primary" onClick={() => changeState('live', 'Пакет выпущен в продажу')}>
            Выпустить в продажу
          </Button>
        ) : p.state === 'live' ? (
          <Button tone="quiet" onClick={() => changeState('stopped', 'Пакет снят с продажи')}>
            Снять с продажи
          </Button>
        ) : (
          <Button tone="primary" onClick={() => changeState('live', 'Пакет снова в продаже')}>
            Вернуть в продажу
          </Button>
        )}

        {/* Удаления у пакета с продажами нет вовсе: у купивших есть
            право на оплаченный состав (БЛ §4.2) */}
        {!sold ? (
          <Button tone="danger" onClick={() => setConfirmDelete(true)}>
            Удалить
          </Button>
        ) : null}
      </PageHeader>

      <Signals>
        {p.state === 'stopped' ? (
          <Note>
            <b>Пакет снят с продажи.</b> Новым клиентам не показывается, но у тех, кто уже купил,
            остаётся, и в отчётах тоже
          </Note>
        ) : null}
        {p.state === 'draft' ? (
          <Alert
            action={
              <Button small tone="quiet" onClick={() => changeState('live', 'Пакет выпущен в продажу')}>
                Выпустить
              </Button>
            }
          >
            <b>Черновик.</b>
            <span>Пакет не появится в витрине, пока вы не выпустите его в продажу</span>
          </Alert>
        ) : null}
        {sold ? (
          <Note tone="warn">
            <b>По пакету уже есть продажи.</b> Состав и перечень номинаций можно только расширять:
            купившие имеют право на то, что оплачивали. Чтобы поменять условия — снимите пакет
            с продажи и создайте новый
          </Note>
        ) : null}
      </Signals>

      {/* Паспорт пакета — ряд цифр обзора, без героев (кит 2.11) */}
      <MetricRow
        variant="sum"
        items={[
          {
            label: 'Первая цена',
            value: money(p.priceFirst),
            sub: p.priceSecond ? `до ${fmtDate(raiseDate)}` : 'не повышается',
          },
          {
            label: 'Вторая цена',
            value: p.priceSecond ? money(p.priceSecond) : '—',
            sub: p.priceSecond
              ? p.raiseAfterQty
                ? `или после ${num(p.raiseAfterQty)} продаж`
                : 'по дате'
              : 'ступеней нет',
          },
          {
            label: 'Услуг в пакете',
            value: num(services.length),
            sub: ofKind('masterclass').length
              ? classesPlural(ofKind('masterclass').length)
              : 'классов нет',
          },
          {
            label: 'Номинаций',
            value: p.nominations.length ? num(p.nominations.length) : '—',
            sub: p.nominations.length
              ? defaults.length
                ? `по умолчанию для ${defaults.length}`
                : 'ни для одной не основной'
              : 'участия нет',
          },
        ]}
      />

      <Columns form>
        <Stack>
          <Panel title="Состав пакета">
            {!services.length && !p.nominations.length ? (
              <EmptyState>
                Состав пуст.
                <br />
                <span className="dim">
                  Добавьте услуги и, если пакет даёт выход на сцену, строку участия
                </span>
              </EmptyState>
            ) : null}

            {p.nominations.length ? (
              <PackageItem
                mark="УЧ"
                title="Участие в номинации"
                sub={`${nomPlural(p.nominations.length)} на выбор · по умолчанию для ${defaults.length}`}
                onRemove={sold ? undefined : removeParticipation}
                removeTitle="Убрать участие"
              />
            ) : null}

            {services.map((s) => (
              <PackageItem
                key={s.id}
                mark={SERVICE_KIND[s.kind].mark}
                title={s.name}
                sub={describeService(event, s)}
                onRemove={sold ? undefined : () => removeService(s.id)}
                removeTitle="Убрать услугу"
              />
            ))}

            <div className="bar">
              {free.length ? (
                <PopoverSelect
                  value={addService}
                  placeholder="Выбрать услугу"
                  onChange={setAddService}
                  options={free.map((s) => ({
                    value: s.id,
                    label: `${SERVICE_KIND[s.kind].label} · ${s.name}`,
                  }))}
                />
              ) : null}
              <Button
                small
                tone="quiet"
                disabled={!addService}
                onClick={() => addService && addServiceToPack(addService)}
              >
                ＋ Услуга
              </Button>
              {!p.nominations.length && event.nominations.length ? (
                <Button
                  small
                  tone="quiet"
                  onClick={() => toggleNomination(event.nominations[0].id)}
                >
                  ＋ Участие
                </Button>
              ) : null}
            </div>

            {!p.nominations.length ? (
              <Note>
                В пакете нет участия — значит, он не даёт выхода на сцену. Так устроены пакеты
                «только классы» и отдельные билеты
              </Note>
            ) : null}
          </Panel>

          {p.nominations.length ? (
            <Panel title="Номинации пакета">
              <Note>
                Участник покупает пакет и вместе с ним участие в одной из этих номинаций.
                Конкретная определяется при регистрации. <b>По умолчанию</b> — та, для которой
                пакет предлагается клиенту; остальные подставляет менеджер
              </Note>

              {event.nominations.map((n) => {
                const link = p.nominations.find((x) => x.nominationId === n.id);
                const others = event.packages.filter(
                  (x) => x.id !== p.id && x.nominations.some((l) => l.nominationId === n.id),
                );

                return (
                  <div
                    key={n.id}
                    className={link ? 'opt on' : 'opt'}
                    onClick={() => (sold && link ? undefined : toggleNomination(n.id))}
                  >
                    <span className="bx" />
                    <div className="t">
                      <div className="n">
                        {n.name} <span className="dim">{ageRange(n.ageFrom, n.ageTo)}</span>
                        {link?.isDefault ? <Pill tone="ok">по умолчанию</Pill> : null}
                      </div>
                      <div className="d">
                        лимит {teamsPlural(n.teamLimit)}
                        {others.length ? ` · ещё в пакетах: ${others.map((x) => x.name).join(', ')}` : ''}
                      </div>
                    </div>
                    {link ? (
                      <>
                        <span className="sp" />
                        <Button
                          small
                          tone="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDefault(n.id);
                          }}
                        >
                          {link.isDefault ? 'снять по умолчанию' : 'сделать по умолчанию'}
                        </Button>
                      </>
                    ) : null}
                  </div>
                );
              })}

              <Note>
                Признак «по умолчанию» эксклюзивный: назначая пакет по умолчанию для номинации,
                система снимает его с прежнего. У каждой номинации ровно один
              </Note>
            </Panel>
          ) : null}
        </Stack>

        <Stack>
          <Panel title="Цена и повышение">
            <Form>
              <NumberField
                label="Первая цена"
                value={p.priceFirst}
                onCommit={(v) => (v ? commit({ priceFirst: v }) : undefined)}
              />
              <NumberField
                label="Вторая цена"
                placeholder="не повышается"
                value={p.priceSecond}
                onCommit={(v) => commit({ priceSecond: v })}
              />
              {p.priceSecond ? (
                <>
                  <DateField
                    label="Повысить по дате"
                    value={raiseDate}
                    onChange={(v) => save({ raiseOn: v })}
                  />
                  <NumberField
                    label="Или после стольких продаж"
                    placeholder="без ограничения"
                    value={p.raiseAfterQty}
                    onCommit={(v) => commit({ raiseAfterQty: v })}
                  />
                </>
              ) : null}
            </Form>

            {p.priceSecond ? (
              <Note>
                Цена поднимется {fmtDate(raiseDate)}
                {p.raiseAfterQty ? ` или после ${salesPlural(p.raiseAfterQty)} — смотря что раньше` : ''}
              </Note>
            ) : (
              <Note>
                Позиция без второй цены не повышается никогда: повторное участие, руководитель
                в составе, SOLO
              </Note>
            )}

            {p.raiseAfterQty ? (
              <>
                <Divider />
                <SectionLabel>Порог повышения</SectionLabel>
                <div className="kv kvr">
                  <span className="k">Продано по первой цене</span>
                  <span className="v">{fraction(p.soldFirstPriceQty, p.raiseAfterQty)}</span>
                </div>
                <span className="bar-mini">
                  <i
                    style={{
                      width: `${Math.min(100, (p.soldFirstPriceQty / p.raiseAfterQty) * 100)}%`,
                      ...(hot ? { background: 'var(--warn)' } : {}),
                    }}
                  />
                </span>
                {hot ? (
                  <Note tone="warn">
                    Осталось {salesPlural(p.raiseAfterQty - p.soldFirstPriceQty)} до автоматического
                    повышения
                  </Note>
                ) : null}
                <Note>
                  Это единственный факт продаж в настройке: он показан потому, что порог прямо
                  над ним и его можно подвинуть
                </Note>
              </>
            ) : null}
          </Panel>

          <Panel title="Браслет">
            <PopoverSelect
              value={p.bandId ?? 'none'}
              onChange={(v) => save({ bandId: v === 'none' ? null : v })}
              options={[
                ...event.bands.map((b) => ({ value: b.id, label: b.name, side: b.purpose ?? '' })),
                { value: 'none', label: 'не выдаётся' },
              ]}
            />
            {band ? (
              <Kv k="Цвет">
                <BandDot color={band.color} />
                {band.name}
              </Kv>
            ) : null}
          </Panel>

          <Panel title="Что даёт пакет">
            <ConditionList>
              <ConditionItem
                done={ofKind('masterclass').length > 0}
                name="Мастер-классы"
                hint={
                  ofKind('masterclass').length
                    ? `${classesPlural(ofKind('masterclass').length)}, конкретные`
                    : 'не входят'
                }
              />
              <ConditionItem
                done={p.nominations.length > 0}
                name="Участие в чемпионате"
                hint={p.nominations.length ? `выбор из ${nomPlural(p.nominations.length)}` : 'не входит'}
              />
              <ConditionItem
                done={ofKind('ticket').length > 0}
                name="Зрительный зал"
                hint={
                  ofKind('ticket').length
                    ? ofKind('ticket')
                        .map((t) => event.blocks.find((b) => b.id === t.blockId)?.name ?? 'без блока')
                        .join(', ')
                    : 'не входит'
                }
              />
              <ConditionItem
                done={ofKind('showcase').length > 0}
                name="Шоукейс"
                hint={
                  ofKind('showcase').length
                    ? `${ofKind('showcase')[0].name}${
                        ofKind('showcase')[0].selection === 'video'
                          ? ' — только после видеоотбора'
                          : ''
                      }`
                    : 'не входит'
                }
              />
              <ConditionItem
                done={Boolean(band)}
                name="Браслет"
                hint={band ? `${band.name} · ${band.purpose ?? ''}` : 'не выдаётся'}
              />
            </ConditionList>
          </Panel>

          <Panel title="Видимость">
            <Switch
              checked={p.visibleToClient}
              onToggle={() => save({ visibleToClient: !p.visibleToClient })}
              label="Виден клиенту"
            />
            <Note>
              {p.visibleToClient
                ? 'Клиент видит пакет в кабинете и может его купить'
                : 'Скрытый пакет: клиент не видит, ставит менеджер'}
            </Note>
          </Panel>

          <Panel title="Состав номера">
            <Switch
              checked={p.isLeader}
              onToggle={() => save({ isLeader: !p.isLeader })}
              label="Пакет руководителя"
            />
            <Note>
              Строка с этим пакетом — руководитель в составе: в проверки возраста номинации она не входит. Признак, а не
              название: пакет можно переименовать.
            </Note>
          </Panel>
        </Stack>
      </Columns>

      {confirmDelete ? (
        <Modal
          title={`Удалить пакет «${p.name}»?`}
          onClose={() => setConfirmDelete(false)}
          confirm={{
            label: 'Удалить',
            danger: true,
            onClick: () =>
              remove.mutate(p.id, {
                onSuccess: () => {
                  toast.show(`Пакет «${p.name}» удалён`);
                  goTab('packages');
                },
                onError: (e) => {
                  setConfirmDelete(false);
                  fail(e);
                },
              }),
          }}
        >
          <p className="txt">Состав и цена пропадут вместе с ним.</p>
          {defaults.length ? (
            <Note tone="warn">
              Пакет стоит по умолчанию для <b>{nomPlural(defaults.length)}</b> — после удаления
              клиенту нечего будет предложить, пока не назначите другой
            </Note>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}

function describeService(event: EventCard, s: EventCard['services'][number]): string {
  if (s.kind === 'masterclass') {
    const time = s.startsAt && s.endsAt ? `${s.startsAt} — ${s.endsAt}` : 'время не задано';
    return s.capacity ? `${time} · ${num(s.capacity)} мест` : time;
  }
  if (s.kind === 'ticket') {
    const block = event.blocks.find((b) => b.id === s.blockId)?.name ?? 'блок удалён';
    return s.capacity ? `${block} · ${num(s.capacity)} мест` : block;
  }
  return [s.choreographer, s.selection === 'video' ? 'видеоотбор' : 'без отбора']
    .filter(Boolean)
    .join(' · ');
}

export type { Package };
